/**
 * Cookie Management Utility
 * Handles cookie consent and actual cookie management.
 */

export type CookieType = "essential" | "analytics" | "marketing" | "functional";

export interface CookiePreferences {
  essential: boolean; // Always true, cannot be disabled
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
}

const COOKIE_CONSENT_KEY = "cookieConsent";
const COOKIE_PREFERENCES_KEY = "cookiePreferences";

/**
 * Get cookie preferences from localStorage
 */
export const getCookiePreferences = (): CookiePreferences | null => {
  const stored = localStorage.getItem(COOKIE_PREFERENCES_KEY);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }
  return null;
};

/**
 * Save cookie preferences to localStorage
 */
export const saveCookiePreferences = (preferences: CookiePreferences): void => {
  localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(preferences));
  localStorage.setItem(COOKIE_CONSENT_KEY, "customized");
  
  // Apply preferences immediately
  applyCookiePreferences(preferences);
};

/**
 * Check if a cookie type is allowed
 */
export const isCookieAllowed = (type: CookieType): boolean => {
  const preferences = getCookiePreferences();
  
  // If no preferences stored, default to essential only
  if (!preferences) {
    return type === "essential";
  }
  
  // Essential cookies are always allowed
  if (type === "essential") {
    return true;
  }
  
  return preferences[type] === true;
};

/**
 * Delete a cookie
 */
export const deleteCookie = (name: string): void => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
};

/**
 * Apply cookie preferences - remove cookies that are not allowed
 */
export const applyCookiePreferences = (preferences: CookiePreferences): void => {
  // Remove analytics cookies if not allowed
  if (!preferences.analytics) {
    // Remove common analytics cookies
    deleteCookie("_ga");
    deleteCookie("_gid");
    deleteCookie("_gat");
    deleteCookie("_ga_");
  }
  
  // Remove marketing cookies if not allowed
  if (!preferences.marketing) {
    // Remove common marketing cookies
    deleteCookie("_fbp");
    deleteCookie("_fbc");
  }
  
  // Remove functional cookies if not allowed
  if (!preferences.functional) {
    // Remove common functional cookies
    deleteCookie("preferences");
    deleteCookie("user_preferences");
  }
};

/**
 * Accept all cookies
 */
export const acceptAllCookies = (): void => {
  const preferences: CookiePreferences = {
    essential: true,
    analytics: true,
    marketing: true,
    functional: true,
  };
  saveCookiePreferences(preferences);
};

/**
 * Decline all non-essential cookies
 */
export const declineNonEssentialCookies = (): void => {
  const preferences: CookiePreferences = {
    essential: true,
    analytics: false,
    marketing: false,
    functional: false,
  };
  saveCookiePreferences(preferences);
};

/**
 * Check if user has given consent
 */
export const hasConsent = (): boolean => {
  return localStorage.getItem(COOKIE_CONSENT_KEY) !== null;
};

